import { ArrowLeft, Calendar, Users } from '@/ui/icons'
import { Link } from 'react-router-dom'
import { Button, Badge } from '@/ui/design-system'
import { formatDateForDisplay } from '@/modules/trips/utils/dateHelpers'

interface GuestTripHeaderProps {
  trip: {
    name: string
    start_date?: string | null
    end_date?: string | null
    members?: Array<{ id: string }>
  }
  guestName: string
  onCreateAccount: () => void
}

// Calculate days until departure
function getDaysUntilDeparture(startDate: string | null | undefined): number | null {
  if (!startDate) return null
  const [year, month, day] = startDate.split('-').map(Number)
  const start = new Date(year, month - 1, day)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffTime = start.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

// Calculate trip duration
function getTripDuration(startDate: string | null | undefined, endDate: string | null | undefined): number | null {
  if (!startDate || !endDate) return null
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number)
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number)
  const start = new Date(startYear, startMonth - 1, startDay)
  const end = new Date(endYear, endMonth - 1, endDay)
  const diffTime = end.getTime() - start.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  return diffDays
}

export function GuestTripHeader({ trip, guestName, onCreateAccount }: GuestTripHeaderProps) {
  const duration = getTripDuration(trip.start_date || null, trip.end_date || null)
  const daysUntil = getDaysUntilDeparture(trip.start_date || null)
  const memberCount = trip.members?.length || 0
  
  // Format start date for display
  const startDateFormatted = trip.start_date ? formatDateForDisplay(trip.start_date) : null

  return (
    <div className="relative w-full overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-adventure-800 via-adventure-700 to-adventure-900">
        {/* Subtle dot pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Top Bar with Back Button and Badge */}
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back</span>
            </Link>
            <Badge 
              variant="secondary" 
              className="bg-white/20 text-white border-white/30 backdrop-blur-sm"
            >
              Guest View
            </Badge>
          </div>

          {/* Trip Title and Guest Info */}
          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              {trip.name}
            </h1>
            <p className="text-base sm:text-lg text-white/90">
              Viewing as {guestName}
            </p>
          </div>

          {/* Info Cards Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            {/* Duration Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <Calendar className="w-5 h-5 text-white/90" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-white/70 uppercase tracking-wide mb-1">
                    Duration
                  </div>
                  <div className="text-lg font-semibold text-white">
                    {duration ? `${duration} ${duration === 1 ? 'day' : 'days'}` : 'Not set'}
                  </div>
                </div>
              </div>
            </div>

            {/* Crew Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <Users className="w-5 h-5 text-white/90" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-white/70 uppercase tracking-wide mb-1">
                    Crew
                  </div>
                  <div className="text-lg font-semibold text-white">
                    {memberCount} {memberCount === 1 ? 'member' : 'members'}
                  </div>
                </div>
              </div>
            </div>

            {/* Days Away Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {daysUntil !== null && daysUntil >= 0 ? (
                    <div className="w-10 h-10 rounded-full bg-adventure-500/30 border-2 border-white/40 flex items-center justify-center">
                      <span className="text-lg font-bold text-white">{daysUntil}</span>
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white/20 border border-white/30" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-white/70 uppercase tracking-wide mb-1">
                    Days Away
                  </div>
                  <div className="text-lg font-semibold text-white">
                    {startDateFormatted 
                      ? startDateFormatted
                      : daysUntil !== null && daysUntil >= 0 
                        ? daysUntil === 0 
                          ? 'Departing today' 
                          : `${daysUntil} ${daysUntil === 1 ? 'day' : 'days'} away`
                        : 'Not set'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Create Account Button */}
          <div className="pt-4">
            <Button
              onClick={onCreateAccount}
              className="bg-white text-adventure-800 hover:bg-white/90 font-semibold px-6 py-2.5 rounded-lg transition-colors"
            >
              Create Account for Full Access
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
